import { useState } from "react";
import { Link } from "react-router-dom";

import { AllRoomsCalendar, type RoomSlot } from "@/components/AllRoomsCalendar";
import { MiniMonthCalendar } from "@/components/MiniMonthCalendar";
import { SlotRequestPanel } from "@/components/SlotRequestPanel";
import { useRooms } from "@/hooks/useRooms";

/**
 * Gesamtansicht über alle Räume. Ergänzt die Raumseiten, ersetzt sie nicht:
 * wer weiß, welchen Raum er will, geht direkt dorthin — wer nur einen Termin
 * sucht, sieht hier, wo überhaupt noch etwas frei ist.
 *
 * Gebucht wird über dasselbe `SlotRequestPanel` wie auf der Raumseite; sobald
 * ein Slot steht, ist der Raum bekannt und der Rest des Ablaufs identisch.
 */
export function CalendarPage() {
  const { data: rooms, isLoading, error } = useRooms();
  const [picked, setPicked] = useState<RoomSlot | null>(null);
  const [calendarDate, setCalendarDate] = useState<Date>(() => new Date());

  if (isLoading) {
    return (
      <div className="mx-auto max-w-container px-6 py-16">
        <p className="text-body-sm text-ink-muted">Kalender wird geladen…</p>
      </div>
    );
  }
  if (error || !rooms || rooms.length === 0) {
    return (
      <div className="mx-auto max-w-container px-6 py-16">
        <p className="mb-4 text-body-sm text-danger">Die Räume konnten nicht geladen werden.</p>
        <Link to="/" className="text-primary underline">
          Zur Raumübersicht
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-container px-6 py-12">
      <header className="mb-8">
        <h1 className="mb-3 font-display text-h1 uppercase text-primary">Alle Räume im Überblick</h1>
        <p className="max-w-3xl text-lead text-ink-soft">
          Verfügbarkeit aller Räume auf einen Blick. In der Tagesansicht steht jeder Raum in
          einer eigenen Spalte — freien Slot wählen und Buchungsanfrage senden. Belegte Zeiten
          sind <span className="font-semibold text-primary">rot</span>, Kurse und gesperrte
          Zeiten <span className="font-semibold text-ink-muted">grau</span>.
        </p>
      </header>

      <section className="mb-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
          <aside className="hidden md:block">
            <MiniMonthCalendar value={calendarDate} onChange={setCalendarDate} />
          </aside>
          <AllRoomsCalendar
            rooms={rooms}
            onSelectSlot={setPicked}
            selected={picked}
            date={calendarDate}
            onNavigate={setCalendarDate}
          />
        </div>
      </section>

      {picked ? (
        <SlotRequestPanel
          room={picked.room}
          value={{ start: picked.start, end: picked.end }}
          onChange={(slot) => setPicked({ room: picked.room, ...slot })}
        />
      ) : (
        <p className="rounded-card border border-line bg-surface p-6 text-body-sm text-ink-muted shadow-card">
          Noch kein Slot gewählt — in der Tagesansicht eine freie Zeit in der Spalte des
          gewünschten Raums antippen.
        </p>
      )}
    </div>
  );
}
